import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time errors anywhere below it. Without this, a crash in any
 * component (e.g. a malformed Gemini response slipping through validation)
 * would white-screen the entire app.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Logged to console in dev; production wires this to Sentry below.
    console.error("[ErrorBoundary]", error, info.componentStack);
    if (typeof window !== "undefined") {
      const sentry = (
        window as unknown as {
          Sentry?: { captureException: (e: unknown, ctx?: unknown) => void };
        }
      ).Sentry;
      sentry?.captureException(error, { contexts: { react: info } });
    }
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div
        role="alert"
        className="bg-bg text-ink flex min-h-screen items-center justify-center p-6"
      >
        <div className="w-full max-w-md space-y-6 rounded-[32px] border border-rose-100 bg-white p-10 text-center shadow-2xl">
          <h1 className="serif text-2xl font-light tracking-tight italic">
            Sherlock crashed
          </h1>
          <p className="text-sm leading-relaxed text-zinc-600">
            Something went wrong while rendering. Your work is safe in local storage —
            refresh or reset to try again.
          </p>
          <pre className="max-h-32 overflow-auto rounded-2xl border border-zinc-100 bg-zinc-50 p-4 text-left text-[10px] text-zinc-500">
            {error.message}
          </pre>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="bg-ink w-full rounded-full py-3 font-medium text-white transition-colors hover:bg-emerald-600"
            >
              Try to recover
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="w-full rounded-full bg-zinc-100 py-3 font-medium text-zinc-600 transition-colors hover:bg-zinc-200"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }
}
